import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import ArchiveIdentifierFormModal from 'src/features/membership/components/forms/identifier/ArchiveIdentifierFormModal';
import DeleteIdentifierFormModal from 'src/features/membership/components/forms/identifier/DeleteIdentifierFormModal';
import UpdateIdentifierFormModal from 'src/features/membership/components/forms/identifier/UpdateIdentifierFormModal';
import { IDENTIFIER_TYPE, MembershipIdentifier } from 'src/features/membership/types';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface CellIdentifierActionsProps extends CellContext<MembershipIdentifier, unknown> {
    membershipId?: string;
    isClient?: boolean;
}

export default function CellIdentifierActions({
    row,
    membershipId = '',
    isClient = false,
}: CellIdentifierActionsProps) {
    const { formatMessage } = useIntl();

    const identifierType = row.original.identifier.type;
    // Archived identifiers are read-only - they cannot be edited, deleted or archived, so hide all row actions.
    const isArchived = !!row.original.is_archived;
    const hideRowActions = isArchived || (isClient && identifierType !== IDENTIFIER_TYPE.LICENSE_PLATE);

    if (hideRowActions) return null;

    return (
        <div className="flex gap-1">
            <TooltipProvider>
                <Tooltip>
                    <UpdateIdentifierFormModal
                        selectedIdentifier={row.original}
                        semantics={{ entryName: formatMessage(identifiersMessages.identifier) }}
                        isClient={isClient}
                        membershipId={membershipId}
                    >
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                            </Button>
                        </TooltipTrigger>
                    </UpdateIdentifierFormModal>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.edit} />
                    </TooltipContent>
                </Tooltip>

                <Tooltip>
                    <DeleteIdentifierFormModal
                        selectedIdentifier={row.original}
                        semantics={{ entryName: formatMessage(identifiersMessages.identifier) }}
                        membershipId={membershipId}
                    >
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                            </Button>
                        </TooltipTrigger>
                    </DeleteIdentifierFormModal>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.delete} />
                    </TooltipContent>
                </Tooltip>

                <Tooltip>
                    <ArchiveIdentifierFormModal
                        selectedIdentifier={row.original}
                        semantics={{ entryName: formatMessage(identifiersMessages.identifier) }}
                        membershipId={membershipId}
                    >
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.ARCHIVE} />
                            </Button>
                        </TooltipTrigger>
                    </ArchiveIdentifierFormModal>
                    <TooltipContent>
                        <FormattedMessage {...identifiersMessages.archiveIdentifierAction} />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
